'use client'
import { LinkExternal } from '@/types/common/LinkExternal'
import s from './Header.module.scss'
import { useContext, useEffect, useState, useRef } from 'react'
import { usePathname, useRouter } from "next/navigation";
import { WebContext } from '@/context/webContext';
import { AnimatePresence, motion } from 'framer-motion'
import Link from 'next/link'
import React from 'react'
import Logo from '../Logo'
import { websiteSchema as websiteSchemaConst, createNavSchema, createWPHeaderSchema } from '@/lib/constants';

interface menuFooter {
  linksSocial: Array<LinkExternal>;
  links: Array<LinkExternal>;
  description: string | null;
  newsletterText: string | null;
  hideNewsletter: boolean | null;
  copyright: string | null;
}
interface FooterProps {
  footer: menuFooter;
}

interface menu {
  links: Array<LinkExternal>
}

interface menuList {
  menu: menu | null;
}

interface DataProps {
  menu: menuList | null;
  footer: FooterProps;
}

export default function Header({ menu, footer }: DataProps) {
  const { backgroundHeader, setBackgroundHeader, colorHeader, setColorHeader } = useContext(WebContext)
  const pathname = usePathname()
  const [hydrated, setHydrated] = useState(false);

  const [activeMenu, setActiveMenu] = useState<string | null>(null)
  const [searchOpen, setSearchOpen] = useState(false); // Estado para abrir/cerrar el formulario
  const searchInputRef = useRef<HTMLInputElement>(null); // Referencia al input
  const searchFormRef = useRef<HTMLFormElement>(null); // Referencia al formulario
  const router = useRouter();
  // Estado del header guardado antes de abrir menú/buscador, para restaurarlo al cerrar
  const savedHeaderState = useRef<{ background: boolean; color: string } | null>(null);

  const [email, setEmail] = useState('');
  const [result, setResult] = useState<{ status: number } | null>(null);

  //SCHEMAS
  const navSchema = createNavSchema(menu?.menu?.links ?? []);
  const wpHeaderSchema = createWPHeaderSchema(menu?.menu?.links ?? []);
  const websiteSchema = websiteSchemaConst;

  // Al navegar: cierra menú/buscador y resetea el header a su estado por defecto
  // (las páginas con hero lo sobrescriben al montar).
  // Solo cuando el pathname cambia de verdad — en el primer montaje el estado
  // inicial del contexto ya es el correcto y resetearlo provocaría un parpadeo
  const prevPathname = useRef(pathname);
  useEffect(() => {
    if (prevPathname.current === pathname) return;
    prevPathname.current = pathname;
    setActiveMenu(null);
    setSearchOpen(false);
    savedHeaderState.current = null;
    setBackgroundHeader(true);
    setColorHeader('black');
  }, [pathname]);

  useEffect(() => {
    const anyOpen = searchOpen || !!activeMenu;

    if (anyOpen) {
      // Guarda el estado solo en la transición cerrado → abierto,
      // no al cambiar de un panel a otro (menú → buscador)
      if (!savedHeaderState.current) {
        savedHeaderState.current = { background: backgroundHeader, color: colorHeader };
      }
      setBackgroundHeader(false);
      setColorHeader('black');
      if (searchOpen) searchInputRef.current?.focus();
    } else if (savedHeaderState.current) {
      setBackgroundHeader(savedHeaderState.current.background);
      setColorHeader(savedHeaderState.current.color);
      savedHeaderState.current = null;
    }
  }, [searchOpen, activeMenu]);

  // Bloquea el scroll de la página mientras el menú o el buscador están abiertos
  useEffect(() => {
    if (!searchOpen && !activeMenu) return;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [searchOpen, activeMenu]);

  // Cerrar el formulario si se hace clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchFormRef.current &&
        !searchFormRef.current.contains(event.target as Node)
      ) {
        setSearchOpen(false);
      }
    };

    if (searchOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [searchOpen]);

  const handleSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const searchQuery = event.currentTarget.search.value.trim()
    if (!searchQuery) return
    router.push(`/search?query=${encodeURIComponent(searchQuery)}`)
    setActiveMenu(null)
    setSearchOpen(false)
  }

  useEffect(() => {
    setHydrated(true);
  }, []);

  const cleanPathname = pathname !== '/' && pathname.endsWith('/')
    ? pathname.slice(0, -1)
    : pathname;

  const subscribeUser = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    try {
      const res = await fetch('/api/subscribeUser', {
        body: JSON.stringify({
          email: { email }
        }),
        headers: {
          'Content-Type': 'application/json'
        },
        method: 'POST'
      })
      setResult({ status: res.status });
      if (res.status === 200) setEmail('');
    } catch {
      setResult({ status: 500 });
    }
  }

  return (
    <>
      <header className={`${s.header} ${backgroundHeader ? s.backgroundHeader : s.transparent} ${colorHeader === 'black' ? s.black : s.white}`}>
        <div
          className={s.logo}
          style={{ transformOrigin: "left center" }}
        >
          <Logo />
        </div>

        <div
          className={s.logoMobile}
          onClick={() => setActiveMenu(null)}
        >
          <Logo />
        </div>

        <div className={s.menu}>
          <nav>
            <ul>
              {menu?.menu?.links.map((link, index) => {

                return (
                  <li key={`menu-link-${index}`} className={cleanPathname === link.url ? s.active : ''}>
                    <Link href={link.url} target={link.newWindow ? '_blank' : '_self'} onClick={() => setActiveMenu(null)}>
                      {link.title}
                    </Link>
                  </li>
                )
              })}
              <li
                key={`menu-link-last`}
                className={s.search}
                role="button"
                tabIndex={0}
                onClick={() => { setSearchOpen(true); setActiveMenu(null) }} // Solo abrir el buscador
                onKeyDown={(e) => { if (e.key === 'Enter') { setSearchOpen(true); setActiveMenu(null) } }}
                style={{ cursor: 'pointer' }}
              >
                SEARCH
              </li>
            </ul>
          </nav>
        </div>

        <div
          className={`${s.burguerMenu} ${activeMenu ? s.active : ''}`}
          role="button"
          tabIndex={0}
          aria-label="Menu"
          aria-expanded={!!activeMenu}
          onClick={() => setActiveMenu(activeMenu === 'active' ? null : 'active')}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setActiveMenu(activeMenu === 'active' ? null : 'active') }}
        >
          <span></span>
          <span></span>
          <span></span>
        </div>

        {hydrated && (
          <>
            <script type="application/ld+json" async dangerouslySetInnerHTML={{ __html: JSON.stringify(websiteSchema) }} />
            <script type="application/ld+json" async dangerouslySetInnerHTML={{ __html: JSON.stringify(navSchema) }} />
            <script type="application/ld+json" async dangerouslySetInnerHTML={{ __html: JSON.stringify(wpHeaderSchema) }} />
          </>
        )}

      </header>
      <AnimatePresence>
        {activeMenu && <motion.div
          className={`${s.headerMobile} ${activeMenu === 'active' ? s.active : ''}`}
          initial={{ opacity: 1, y: '-100%' }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 1, y: '-100%' }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
        >
          <div className={s.menu}>
            <nav>
              <ul>
                {menu?.menu?.links.map((link, index) => (
                  <li key={`menu-link-${index}`} className={cleanPathname === link.url ? s.active : ''}>
                    <Link href={link.url} target={link.newWindow ? '_blank' : '_self'} onClick={() => setActiveMenu(null)}>
                      {link.title}
                    </Link>
                  </li>
                ))}
                <li
                  key={`menu-link-last`}
                  className={s.search}
                  role="button"
                  tabIndex={0}
                  onClick={() => { setSearchOpen(true); setActiveMenu(null) }} // Solo abrir el buscador
                  onKeyDown={(e) => { if (e.key === 'Enter') { setSearchOpen(true); setActiveMenu(null) } }}
                  style={{ cursor: 'pointer' }}
                >
                  SEARCH
                </li>
              </ul>
            </nav>
          </div>
          <div className={s.footerElements}>
            <div className={s.menus}>
              <nav className={s.social}>
                <ul>
                  {footer.footer.linksSocial.map((link, index) => (
                    <li key={`link-social-${index}`}>
                      <Link href={link.url} target={link.newWindow ? '_blank' : '_self'}>
                        {link.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </nav>
              <nav className={s.social}>
                <ul>
                  {footer.footer.links.map((link, index) => (
                    <li key={`link-politics-${index}`}>
                      <Link href={link.url} target={link.newWindow ? '_blank' : '_self'} onClick={() => setActiveMenu(null)}>
                        {link.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </nav>
            </div>
            {!footer.footer.hideNewsletter && <div className={s.newsletter}>
              <div className={s.newsletterText}>
                <p>NEWSLETTER</p>
                <p>
                  {footer.footer.newsletterText}
                </p>
              </div>

              <form onSubmit={subscribeUser}>
                <input type="email" placeholder="Your email" required onChange={(e) => setEmail(e.target.value)} value={email} />
                <button>JOIN</button>
              </form>
              <div className={s.answer}>{result && result.status == 200 ? <p>{"Thank you for subscribing"}</p> : result && result.status == 400 ? <p>{"You're already subscribed"}</p> : result ? <p>{"Something went wrong. Please try again."}</p> : undefined}</div>
            </div>
            }
            <div className={s.copyright}>
              <p>
                {footer.footer.copyright}
                <br />
                Code by <a href="https://www.ama.work" target="_blank" rel="noopener noreferrer">AMA</a>
              </p>
            </div>

          </div>
        </motion.div>}
      </AnimatePresence>
      {/* Search Form (Mostrado solo cuando searchOpen es true) */}
      {searchOpen && (
        <div className={`${s.searchForm}`}>
          <form onSubmit={handleSearch} ref={searchFormRef}>
            <input
              type="text"
              name="search"
              ref={searchInputRef} // Referencia al input
            />
            <button>Search</button>
          </form>
        </div>
      )}
    </>
  );
}